import type { Count } from '../lib/format'

import type { Translations } from './types'

/**
 * « 25 წთ-ში », but « 1 დღეში »: the case ending joins an abbreviation with a
 * hyphen, while an abbreviated « დღ. » takes the ending in place of its dot.
 */
const inTime = (duration: string): string => (duration.endsWith('დღ.') ? `${duration.slice(0, -1)}ეში` : `${duration}-ში`)

/**
 * Georgian. Typed against the Russian source: every key and every function
 * signature must match. Georgian nouns do not change after a numeral
 * (« 1 გამომწერი », « 5 გამომწერი »), so no plural helper is needed. Amounts
 * and dates arrive already formatted; the dictionary supplies only the words
 * around them. Georgian has no capital letters, so nothing is capitalised.
 */
export const ka: Translations = {
  common: {
    all: 'მაქს.',
    back: 'უკან',
    cancel: 'გაუქმება',
    confirm: 'დადასტურება',
    copy: 'კოპირება',
    copied: 'დაკოპირდა',
    open: 'გახსნა',
    retry: 'თავიდან ცდა',
    save: 'შენახვა',
    close: 'დახურვა',
    loading: 'იტვირთება',
    available: 'ხელმისაწვდომი',
    total: 'სულ',
    amount: 'თანხა',
    network: 'ქსელი',
    asset: 'ვალუტა',
    status: 'სტატუსი',
    date: 'თარიღი',
    nothingYet: 'ჯერ ცარიელია',
    somethingWentWrong: 'რაღაც არასწორად წავიდა',
    reload: 'გადატვირთვა',
    crashHint: 'ეკრანის ჩვენება ვერ მოხერხდა. გადატვირთეთ აპლიკაცია — თანხები და განაცხადები უსაფრთხოდაა.',
  },

  nav: {
    label: 'მთავარი ნავიგაცია',
    home: 'მთავარი',
    markets: 'ბაზრები',
    signals: 'სიგნალები',
    trade: 'ვაჭრობა',
    wallet: 'აქტივები',
  },

  auth: {
    signIn: 'შესვლა',
    signInSubtitle: 'შედით, რომ დაუბრუნდეთ სიგნალებსა და ბალანსს',
    signInAction: 'შესვლა',
    signUp: 'რეგისტრაცია',
    signUpSubtitle: 'ელფოსტა და პაროლი — ეს საკმარისია დასაწყებად',
    signUpSubtitleInvite: 'ელფოსტა, პაროლი და მოწვევის კოდი — ეს საკმარისია დასაწყებად',
    email: 'ელფოსტა',
    emailPlaceholder: 'name@example.com',
    password: 'პაროლი',
    passwordHint: 'მინიმუმ 8 სიმბოლო, ასოები და ციფრები',
    passwordConfirm: 'გაიმეორეთ პაროლი',
    referralCode: 'მოწვევის კოდი',
    referralCodePlaceholder: 'მაგალითად, 8K3M7QPL',
    referralCodeHint: 'არასავალდებულო — თუ 19CRT-ის წევრმა მოგიწვიათ',
    referralCodeRequiredHint: 'რეგისტრაცია მხოლოდ მოწვევით: გამოიყენეთ იმის ბმული ან კოდი, ვინც მოგიწვიათ',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} ბონუს სიგნალი` : null,
        promo === null ? null : `პრომო-ბონუსი ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `თუ კოდი 19CRT-ის წევრმა მოგცათ, მოწვევა ჩაითვლება მინიმუმ ${deposit} ${asset}-ის პირველი დეპოზიტის შემდეგ`
        : `თუ კოდი 19CRT-ის წევრმა მოგცათ, მინიმუმ ${deposit} ${asset}-ის პირველი დეპოზიტის შემდეგ მიიღებთ: ${perks.join(' და ')}`
    },
    forgot: 'დაგავიწყდათ პაროლი?',
    createAccount: 'ანგარიშის შექმნა',
    haveAccount: 'უკვე გაქვთ ანგარიში?',
    noAccount: 'არ გაქვთ ანგარიში?',
    backToSignIn: 'შესვლაზე დაბრუნება',
    verifyTitle: 'დაადასტურეთ ელფოსტა',
    verifySent: (email: string) => `წერილი გაიგზავნა მისამართზე ${email}`,
    verifySentAnywhere: 'ბმულიანი წერილი გაიგზავნა თქვენს ელფოსტაზე',
    verifyHint:
      'გახსენით ბმული წერილიდან, რომ დაადასტუროთ მისამართი: მასზე იგზავნება წვდომის აღდგენის წერილები. თუ წერილი არ ჩანს, შეამოწმეთ საქაღალდე „სპამი“.',
    verifyEmailPrompt: 'მიუთითეთ ელფოსტა, რომლითაც ანგარიშია დარეგისტრირებული',
    verifyResend: 'წერილის ხელახლა გაგზავნა',
    verifyResendIn: (seconds: Count) => `ხელახლა გაგზავნა შესაძლებელია ${seconds.text} წმ-ში`,
    verifyResent: 'კიდევ ერთი წერილი გავაგზავნეთ',
    verifyResentMaybe: 'თუ მისამართი რეგისტრირებულია და ჯერ არ არის დადასტურებული, ახალი წერილი უკვე გზაშია',
    verifyChecking: 'ბმულს ვამოწმებთ',
    verifyDone: 'ელფოსტა დადასტურებულია',
    verifyDoneHint: 'ვხსნით აპლიკაციას',
    verifyDoneSignInHint: 'ვხსნით შესვლას',
    resetTitle: 'პაროლის აღდგენა',
    resetSubtitle: 'მიუთითეთ ელფოსტა — გამოგიგზავნით ბმულს პაროლის შესაცვლელად',
    resetSend: 'ბმულის გაგზავნა',
    resetSent: 'თუ ასეთი ანგარიში არსებობს, წერილი უკვე გაგზავნილია',
    resetSentHint:
      'ჩვენ არ ვაჩვენებთ, დარეგისტრირებულია თუ არა მისამართი: სხვაგვარად ამ ფორმით სხვისი ელფოსტების შემოწმება იქნებოდა შესაძლებელი. შეამოწმეთ „შემოსული“ და „სპამი“.',
    newPassword: 'ახალი პაროლი',
    newPasswordSubtitle: 'მოიფიქრეთ ახალი პაროლი. ძველი მაშინვე შეწყვეტს მუშაობას.',
    newPasswordSubmit: 'პაროლის შენახვა',
    requestNewLink: 'ახალი ბმულის მოთხოვნა',
    newPasswordDone: 'პაროლი განახლდა',
    newPasswordDoneHint: 'ვხსნით შესვლას',
    signOut: 'გასვლა',
    totpCode: 'დადასტურების კოდი',
    totpHint: 'ექვსი ციფრი ავთენტიფიკატორის აპლიკაციიდან',
    recoveryCode: 'აღდგენის კოდი',
    recoveryCodeHint: 'რვა სიმბოლო იმ კოდებიდან, რომლებიც 2FA-ს ჩართვისას შეინახეთ',
    useRecoveryCode: 'აღდგენის კოდით შესვლა',
    useAppCode: 'აპლიკაციის კოდის შეყვანა',
    resetLinkInvalid: 'ბმული არ მუშაობს',
    errors: {
      invalidCredentials: 'არასწორი ელფოსტა ან პაროლი',
      emailTaken: 'ეს ელფოსტა უკვე დარეგისტრირებულია',
      weakPassword: 'პაროლი ძალიან მოკლეა',
      passwordRule: 'პაროლი უნდა შეიცავდეს ასოებსა და ციფრებს',
      passwordsDiffer: 'პაროლები არ ემთხვევა',
      invalidEmail: 'შეიყვანეთ მისამართი ფორმატით name@example.com',
      invalidReferralCode: 'შეამოწმეთ მოწვევის კოდი',
      referralRequired: 'შეიყვანეთ მოწვევის კოდი',
      emailRequired: 'მიუთითეთ ელფოსტა',
      passwordRequired: 'შეიყვანეთ პაროლი',
      accountBlocked: 'ანგარიშზე წვდომა შეზღუდულია. მოგვწერეთ მხარდაჭერას',
      rateLimited: 'ძალიან ბევრი მცდელობა. სცადეთ მოგვიანებით',
      totpRequired: 'შეიყვანეთ კოდი ავთენტიფიკატორის აპლიკაციიდან',
      totpInvalid: 'კოდი არასწორია. შეამოწმეთ ტელეფონის დრო და შეიყვანეთ ახალი',
      totpFormat: 'აპლიკაციის კოდი ექვსი ციფრია, აღდგენის კოდი — რვა სიმბოლო',
      expiredToken: 'ბმული მოძველდა. მოითხოვეთ ახალი',
      verifyLinkUsed: 'ეს ბმული უკვე გამოყენებულია. შედით — ან გახსენით ბოლო წერილი, თუ ახალი მოითხოვეთ',
      missingToken: 'ბმული არასრულია. გახსენით ის წერილიდან მთლიანად',
      validation: 'შეამოწმეთ შევსებული ველები',
      network: 'სერვერთან კავშირი არ არის. შეამოწმეთ ინტერნეტი',
    },
  },

  home: {
    promoLabel: 'პრომო',
    banners: [
      {
        title: 'ოქროს სიგნალები',
        subtitle: 'კოდი ჩვენი Telegram-არხიდან — გარიგება ერთ ნაბიჯში',
        cta: 'კოდის შეყვანა',
      },
      {
        title: 'ბაზრები რეალურ დროში',
        subtitle: 'ცოცხალი გრაფიკები და ორდერების წიგნი ყველა წყვილზე',
        cta: 'ბაზრების გახსნა',
      },
      {
        title: 'შეავსეთ წუთებში',
        subtitle: 'USDT ქსელებში TRC20 და ERC20',
        cta: 'ანგარიშის შევსება',
      },
    ],
    pauseAnnouncements: 'განცხადებების შეჩერება',
    resumeAnnouncements: 'განცხადებების გაგრძელება',
    announcementsLabel: 'განცხადებები',
    announcements: [
      'შევსებამდე შეამოწმეთ გადარიცხვის ვალუტა და ქსელი',
      'ანგარიშის დაცვას მართეთ განყოფილებაში „უსაფრთხოება“',
      'ოპერაციების ისტორია ხელმისაწვდომია განყოფილებაში „აქტივები“',
    ],
    totalBalance: 'საერთო ბალანსი',
    quickTrade: 'სწრაფი ვაჭრობა',
    quickTradeHint: 'სპოტი და ფიუჩერსები ერთ ტერმინალში',
    quickActions: 'სწრაფი მოქმედებები',
    signalLive: 'სიგნალი ხელმისაწვდომია',
    signalNext: 'შემდეგი სიგნალი',
    signalClosesIn: 'დახურვამდე',
    signalOpensAt: (time: string) => `${time}-ზე გაიხსნება`,
    signalsTitle: 'ოქროს სიგნალები',
    allSignals: 'ყველა სიგნალი',
    marketMoves: 'ბაზრის მოძრაობა',
    pair: 'წყვილი',
    lastPrice: 'ფასი',
    change24h: 'ცვლილება 24სთ',
    /** After the figure in the home balance's change pill: « +1.30% 24სთ-ში ». */
    change24hShort: '24სთ-ში',
    volume24h: 'მოცულობა 24სთ',
    allMarkets: 'ყველა ბაზარი',
    pricesUnavailable: 'კურსები მიუწვდომელია — მთელი პორტფელის შეფასება ვერ ხერხდება',
    ratesUnavailable: 'კურსები დროებით მიუწვდომელია',
    marketsUnavailable: 'ბაზრების სია დროებით მიუწვდომელია',
    signalsNone: 'ახლა სიგნალები არ არის — ახალი კოდები ჩვენს Telegram-არხზე ქვეყნდება',
  },

  markets: {
    title: 'ბაზრები',
    search: 'წყვილის ძიება',
    spot: 'სპოტი',
    futures: 'ფიუჩერსები',
    gainers: 'ზრდის ლიდერები',
    losers: 'კლება',
    volume: 'მოცულობა 24სთ',
    volumeShort: 'მოცულობა',
    typeLabel: 'ბაზრის ტიპი',
    sortLabel: 'დალაგება',
    clearSearch: 'ძიების გასუფთავება',
    favourites: 'რჩეულები',
    addFavourite: 'რჩეულებში დამატება',
    removeFavourite: 'რჩეულებიდან ამოღება',
    emptyFavourites: 'რჩეულებში ჯერ ცარიელია — მონიშნეთ წყვილები ვარსკვლავით',
    empty: 'ვერაფერი მოიძებნა',
    emptyHint: 'შეამოწმეთ წყვილის სიმბოლო — მაგალითად, BTC ან SOL.',
    emptySpot: 'სპოტური წყვილები ჯერ არ არის გახსნილი',
    emptyFutures: 'ფიუჩერსული წყვილები ჯერ არ არის გახსნილი',
    loadFailed: 'ბაზრების სიის ჩატვირთვა ვერ მოხერხდა',
    pricesUnavailable: 'კოტირებები დროებით მიუწვდომელია. ფასები ავტომატურად განახლდება.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'ოქრო' } as Record<string, string>,
  },

  wallet: {
    title: 'აქტივები',
    totalValue: 'ანგარიშის საერთო ღირებულება',
    accounts: 'ჩემი ანგარიშები',
    latestWithdrawal: 'ბოლო გატანა',
    coin: 'მონეტა',
    searchCoin: 'მონეტის ძიება',
    quickActions: 'სწრაფი მოქმედებები',
    coins: 'მონეტები',
    hideBalance: 'ბალანსის დამალვა',
    showBalance: 'ბალანსის ჩვენება',
    loadError: 'ბალანსის ჩატვირთვა ვერ მოხერხდა',
    pricesUnavailable: 'კურსები მიუწვდომელია — მთელი პორტფელის შეფასება ვერ ხერხდება',
    coinsEmpty: 'მონეტები ჯერ არ არის. შეავსეთ ანგარიში — ისინი აქ გამოჩნდება.',
    accountSpot: 'სპოტური ანგარიში',
    accountSpotHint: 'ხელმისაწვდომია ვაჭრობისთვის',
    accountFutures: 'ფიუჩერსული ანგარიში',
    accountFuturesHint: 'პოზიციების მარჟა',
    accountCopy: 'კოპირება (არქივი)',
    accountCopyHint: 'გადაიტანეთ სპოტზე',
    actions: {
      deposit: 'შევსება',
      withdraw: 'გატანა',
      transfer: 'გადარიცხვა',
      history: 'ისტორია',
    },
    deposit: {
      title: 'შევსება',
      history: 'შევსებების ისტორია',
      chain: 'ქსელი',
      saveQr: 'QR-კოდის შენახვა',
      address: 'შევსების მისამართი',
      copyAddress: 'მისამართის კოპირება',
      notice: 'მნიშვნელოვანი შეტყობინება',
      noticeWrongNetwork: (asset: string, network: string) =>
        `გამოაგზავნეთ მხოლოდ ${asset} ${network} ქსელით. სხვა ქსელიდან გამოგზავნილი მონეტები დაიკარგება.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `ჩარიცხვა ქსელის ${count.text} დადასტურების შემდეგ, ჩვეულებრივ დაახლოებით ${minutes.text} წუთში.`,
      noticeMinimum: (amount: string, asset: string) =>
        `შევსების მინიმალური თანხა — ${amount} ${asset}.`,
      waiting: 'ველოდებით გადარიცხვას',
      incoming: 'გზაში მყოფი შევსებები',
      incomingFirst: 'გადარიცხვა ქსელში გამოჩნდა, ველოდებით პირველ დადასტურებას',
      incomingProgress: (done: Count, total: Count) => `დადასტურებები: ${total.text}-დან ${done.text}`,
      selectAsset: 'აირჩიეთ ვალუტა',
      noAssets: 'შევსება დროებით მიუწვდომელია. სცადეთ მოგვიანებით.',
      noNetworks: 'ამ მონეტისთვის ქსელები ჯერ არ არის — აირჩიეთ სხვა.',
      addressFailed: 'შევსების მისამართის მიღება ვერ მოხერხდა',
      noticePermanent: 'მისამართი მუდმივია: გამოიყენეთ ის ყველა შემდეგი შევსებისთვის.',
      qrSaved: 'QR-კოდი შენახულია',
      qrFailed: 'QR-კოდის შენახვა ვერ მოხერხდა',
      paymentLink: 'ბმული საფულისთვის',
      paymentLinkHint: 'გახსენით საფულეში — ქსელი და მისამართი ავტომატურად შეივსება.',
      copyLink: 'ბმულის კოპირება',
    },
    withdraw: {
      title: 'გატანა',
      history: 'გატანების ისტორია',
      asset: 'გატანის ვალუტა',
      selectAsset: 'აირჩიეთ ვალუტა',
      network: 'გატანის ქსელი',
      address: 'გატანის მისამართი',
      addressPlaceholder: (network: string) => `შეიყვანეთ ${network} მისამართი`,
      addressPaste: 'მისამართის ჩასმა',
      addressFromBook: (label: string): string => `დადასტურებულია მისამართების წიგნში · ${label}`,
      paste: 'ჩასმა',
      addressInvalid: (network: string) => `${network} ქსელის მისამართს არ ჰგავს`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `ეს ${detected} ქსელის მისამართია, არჩეულია კი ${selected}`,
      amount: 'გატანის თანხა',
      amountPlaceholder: '0.00',
      networkFee: 'ქსელის საკომისიო',
      platformFee: (rate: string): string => `პლატფორმის საკომისიო (${rate}%)`,
      feeFree: 'ახლა საკომისიოს გარეშე',
      feeDynamic: 'გაზის მიმდინარე ფასით',
      willReceive: 'მისაღები',
      notice: 'მნიშვნელოვანი შეტყობინება',
      noticeReview: 'ყოველი განაცხადი გაგზავნამდე მოწმდება. თანხა მაშინვე რეზერვდება.',
      noticeTiming: 'გაგზავნა დამტკიცებიდან 24 საათის განმავლობაში.',
      noticeMinimum: (amount: string, asset: string) =>
        `გატანის მინიმალური თანხა — ${amount} ${asset}.`,
      noticeSupport:
        'შეამოწმეთ მისამართი თავად: სხვის მისამართზე გადარიცხვის დაბრუნება შეუძლებელია. სადავო განაცხადებს მხარდაჭერა განაცხადის ნომრით არჩევს.',
      insufficient: 'არასაკმარისი თანხა',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `დღიური ლიმიტი ${limit} USDT, დღეს დარჩა ${remaining} USDT.` : `ვერიფიკაციის გარეშე — დღეში ${limit} USDT-მდე, დარჩა ${remaining}. გაიარეთ ვერიფიკაცია ლიმიტის ასაწევად.`,
      addressNotConfirmed: 'გატანა მხოლოდ მისამართების წიგნის ელფოსტით დადასტურებულ მისამართებზე',
      limitReached: (verified: boolean): string => (verified ? 'გატანის დღიური ლიმიტი ამოწურულია' : 'ვერიფიკაციის გარეშე დღიური ლიმიტი ამოწურულია — გაიარეთ ვერიფიკაცია პროფილში'),
      belowMinimum: (amount: string, asset: string) => `მინიმუმ ${amount} ${asset}`,
      confirmTitle: 'შეამოწმეთ განაცხადი',
      confirmSubtitle: 'გაგზავნის შემდეგ გატანის გაუქმება შეუძლებელია. გადაამოწმეთ ქსელი და მისამართი.',
      confirmAction: 'გატანის დადასტურება',
      failed: 'განაცხადის შექმნა ვერ მოხერხდა',
      submitted: 'განაცხადი შექმნილია',
      submittedHint: 'შემოწმების შემდეგ თანხა ქსელში გაიგზავნება. განაცხადის სტატუსი გატანის განაცხადების სიაშია.',
      requestId: 'განაცხადის ნომერი',
      statusPending: 'მოწმდება',
      toRequests: 'გატანის განაცხადებზე',
      empty: 'ამჟამად გასატანად ხელმისაწვდომი ვალუტები არ არის',
      loadFailed: 'ვალუტების სიის ჩატვირთვა ვერ მოხერხდა',
      routeUnavailable: 'ამ ქსელში გატანა ახლა მიუწვდომელია — აირჩიეთ სხვა ქსელი',
      invalidAmount: 'თანხა არ გამოდგება: შეამოწმეთ სიზუსტე და ქსელის საკომისიო',
      edit: 'განაცხადის შესწორება',
    },
    withdrawals: {
      title: 'გატანის განაცხადები',
      toAddress: (address: string): string => `${address}-ზე`,
      note: 'უარყოფილი და წარუმატებელი განაცხადები რეზერვს ანგარიშზე აბრუნებს. გახსენით განაცხადი, რომ ნახოთ ტრანზაქცია ან მიზეზი.',
      empty: 'გატანის განაცხადები ჯერ არ არის. აქ გამოჩნდება თითოეული განაცხადის სტატუსი — შემოწმებიდან ქსელის დადასტურებამდე.',
      loadError: 'განაცხადების ჩატვირთვა ვერ მოხერხდა',
      details: 'გატანის განაცხადი',
      created: 'შეიქმნა',
      transaction: 'ტრანზაქცია',
      openExplorer: 'ექსპლორერში გახსნა',
      reason: 'მიზეზი',
      status: {
        pending: 'მოწმდება',
        approved: 'დამტკიცებულია',
        rejected: 'უარყოფილია',
        broadcasting: 'იგზავნება',
        sent: 'გაგზავნილია',
        confirmed: 'დადასტურებულია',
        failed: 'ვერ შესრულდა',
      },
      statusHint: {
        pending: 'განაცხადი მოწმდება. თანხა ანგარიშზე დარეზერვებულია.',
        approved: 'შემოწმება გავლილია, განაცხადი ქსელში გაგზავნას ელოდება.',
        rejected: 'განაცხადი უარყოფილია, რეზერვი ანგარიშზე დაბრუნდა.',
        broadcasting: 'ტრანზაქცია ქსელში იგზავნება.',
        sent: 'ტრანზაქცია ქსელშია, ველოდებით დადასტურებას.',
        confirmed: 'ქსელმა ტრანზაქცია დაადასტურა. თანხა მიწოდებულია.',
        failed: 'გაგზავნა ვერ მოხერხდა, რეზერვი ანგარიშზე დაბრუნდა.',
      },
    },
    transfer: {
      title: 'გადარიცხვა',
      from: 'საიდან',
      fromAvailable: (amount: string): string => `საიდან · ხელმისაწვდომია ${amount}`,
      to: 'სად',
      submit: 'გადარიცხვა',
      swap: 'ანგარიშების გაცვლა',
      selectFrom: 'ჩამოჭრის ანგარიში',
      selectTo: 'ჩარიცხვის ანგარიში',
      selectAsset: 'აირჩიეთ მონეტა',
      after: 'გადარიცხვის შემდეგ',
      insufficient: 'ჩამოჭრის ანგარიშზე არასაკმარისი თანხაა',
      done: 'გადარიცხვა შესრულდა',
      hint: 'საკუთარ ანგარიშებს შორის გადარიცხვები მყისიერი და უფასოა.',
      empty: 'გადასარიცხი ჯერ არაფერია — ჯერ შეავსეთ ანგარიში.',
      failed: 'გადარიცხვა ვერ შესრულდა. სცადეთ თავიდან',
    },
    history: {
      title: 'ოპერაციების ისტორია',
      deposit: 'შევსება',
      withdrawal: 'გატანა',
      trade: 'გარიგება',
      fee: 'საკომისიო',
      transfer: 'გადარიცხვა',
      pnl: 'შედეგი',
      empty: 'ოპერაციები ჯერ არ არის. აქ გამოჩნდება შევსებები, გატანები და გარიგებები.',
      emptyFiltered: 'ამ ფილტრით ოპერაციები არ არის',
      loadError: 'ისტორიის ჩატვირთვა ვერ მოხერხდა',
      today: 'დღეს',
      yesterday: 'გუშინ',
      balanceAfter: (amount: string) => `ნაშთი ${amount}`,
      onlyAccount: (account: string) => `მხოლოდ ${account}`,
      resetAccount: 'ყველა ანგარიშის ჩვენება',
      accountFilter: 'ანგარიში',
      allAccounts: 'ყველა ანგარიში',
      withdrawalsLink: 'გატანის განაცხადების სტატუსები',
      badges: {
        credited: 'ჩაირიცხა',
        sent: 'გაიგზავნა',
        fee: 'საკომისიო',
        reserved: 'დარეზერვდა',
        returned: 'დაბრუნდა',
        released: 'გათავისუფლდა',
        filled: 'შესრულდა',
        settled: 'დაანგარიშდა',
        liquidated: 'ლიკვიდაცია',
        done: 'შესრულებულია',
        adjusted: 'კორექტირება',
        paid: 'გადახდილია',
      },
      filters: {
        all: 'ყველა',
        rewards: 'ბონუსები',
        deposits: 'შევსებები',
        withdrawals: 'გატანები',
        trades: 'გარიგებები',
        transfers: 'გადარიცხვები',
      },
      types: {
        deposit: 'შევსება',
        withdrawal: 'გატანა',
        withdrawal_fee: 'გატანის საკომისიო',
        withdrawal_platform_fee: 'პლატფორმის საკომისიო გატანაზე',
        withdrawal_lock: 'რეზერვი გატანისთვის',
        withdrawal_release: 'გატანის რეზერვის დაბრუნება',
        order_lock: 'რეზერვი ლიმიტური ორდერისთვის',
        order_release: 'ორდერის რეზერვის დაბრუნება',
        bonus: 'პრომო-ბონუსი',
        trade_buy: 'ყიდვა',
        trade_sell: 'გაყიდვა',
        trading_fee: 'სავაჭრო საკომისიო',
        realized_pnl: 'გარიგების შედეგი',
        signal_programmed_profit: 'სიგნალის პროგრამული მოგება',
        funding: 'ფანდინგი',
        margin_lock: 'მარჟის რეზერვი',
        margin_release: 'მარჟის დაბრუნება',
        liquidation: 'ლიკვიდაცია',
        transfer_in: 'გადარიცხვა ანგარიშზე',
        transfer_out: 'გადარიცხვა ანგარიშიდან',
        copy_allocation: 'გამოყოფილია კოპირებისთვის',
        copy_release: 'დაბრუნება კოპირებიდან',
        copy_commission: 'კოპი-ტრეიდინგის საკომისიო',
        referral: 'რეფერალური დარიცხვა',
        adjustment: 'კორექტირება',
      },
    },
  },

  trade: {
    title: 'ვაჭრობა',
    menu: {
      account: 'ანგარიში',
      trading: 'ვაჭრობა',
      more: 'სხვა',
    },
    long: 'ლონგი',
    short: 'შორტი',
    buy: 'ყიდვა',
    sell: 'გაყიდვა',
    marketOrder: 'საბაზრო',
    limitOrder: 'ლიმიტური',
    price: 'ფასი',
    marketPrice: 'ბაზრის ფასით',
    quantity: 'რაოდენობა',
    leverage: 'ბერკეტი',
    takeProfitStopLoss: 'ტეიკ-პროფიტი / სტოპ-ლოსი',
    positions: 'პოზიციები',
    orders: 'ორდერები',
    history: 'ისტორია',
    copyTrading: 'ქოფი-ტრეიდინგი',
    unrealizedPnl: 'არარეალიზ. P&L',
    unrealizedPnlShort: 'P&L',
    margin: 'მარჟა',
    entry: 'შესვლის ფასი',
    size: 'ზომა',
    liquidation: 'ლიკვიდაცია',
    close: 'დახურვა',
    orderBook: 'ორდერების წიგნი',
    noPositions: 'განათავსეთ ორდერი ზემოთ და პოზიცია აქ გამოჩნდება.',
    balance: 'ბალანსი',
    perpetual: 'უვადო',
    perpShort: 'Perp',
    fundingIn: (time: string) => `${time}-ში`,
    position: 'პოზიცია',
    closeAtMarket: 'საბაზრო ფასით დახურვა',
    keepPosition: 'პოზიციის შენარჩუნება',
    placeBuyOrder: 'ყიდვის ორდერის განთავსება',
    placeSellOrder: 'გაყიდვის ორდერის განთავსება',
    placeLongOrder: 'ლონგ ორდერის განთავსება',
    placeShortOrder: 'შორტ ორდერის განთავსება',
    isolatedMargin: 'იზოლირებული მარჟა',
    crossMargin: 'კროს-მარჟა',
    markShort: 'მარკ-ფასი',
    liqPrice: 'ლიკვ. ფასი',
    noPositionsTitle: 'ღია პოზიციები არ არის',
    noOrdersTitle: 'აქტიური ორდერები არ არის',
    noHistoryTitle: 'გარიგებები ჯერ არ არის',
    pair: 'წყვილი',
    selectPair: 'აირჩიეთ წყვილი',
    sideLabel: 'მიმართულება',
    orderType: 'ორდერის ტიპი',
    sizeShare: 'ხელმისაწვდომის წილი',
    quantityShort: 'რაოდ.',
    takeProfit: 'ტეიკ-პროფიტი',
    stopLoss: 'სტოპ-ლოსი',
    tabsLabel: 'ჩემი გარიგებები',
    openChart: 'გრაფიკი',
    openLong: (symbol: string) => `ლონგი ${symbol}`,
    openShort: (symbol: string) => `შორტი ${symbol}`,
    buyAsset: (symbol: string) => `${symbol}-ის ყიდვა`,
    sellAsset: (symbol: string) => `${symbol}-ის გაყიდვა`,
    enterQuantity: 'მიუთითეთ რაოდენობა',
    insufficient: 'ანგარიშზე არასაკმარისი თანხაა',
    belowMinNotional: (amount: string, asset: string) =>
      `ორდერის მინიმალური მოცულობა — ${amount} ${asset}`,
    bookEmpty: 'ორდერების წიგნში განაცხადები ჯერ არ არის',
    bookUnavailable: 'ორდერების წიგნი მიუწვდომელია',
    tradingUnavailable:
      'სავაჭრო სერვისი არ პასუხობს — ორდერის გაგზავნა ახლა შეუძლებელია. ბალანსები, შევსება და გატანა ჩვეულებრივ მუშაობს.',
    marketsFailed: 'წყვილების სიის ჩატვირთვა ვერ მოხერხდა',
    noMarkets: 'სავაჭრო წყვილები ჯერ არ არის — ისინი ბაზრების გაშვებასთან ერთად გამოჩნდება.',
    confirmTitle: 'შეამოწმეთ ორდერი',
    confirmSubtitle: 'შესრულებული ორდერის გაუქმება შეუძლებელია.',
    marginRequired: 'გამოყენებული მარჟა',
    cost: 'გადასახდელი',
    liquidationEstimate: 'სავარაუდო ლიკვიდაცია',
    submitted: 'ორდერი გაგზავნილია',
    submitFailed: 'ორდერის გაგზავნა ვერ მოხერხდა',
    futuresSoon: 'ფიუჩერსები დროებით მიუწვდომელია. სპოტური ვაჭრობა მუშაობს.',
    marginMode: 'მარჟა',
    cross: 'კროსი',
    isolated: 'იზოლირებული',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'კროსი: პოზიციას მთელი ფიუჩერსული ბალანსი უჭერს მხარს'
        : 'იზოლირებული: რისკავს მხოლოდ ამ პოზიციის მარჟა',
    reduceOnly: 'მხოლოდ დახურვა',
    futuresAccount: 'ფიუჩერსული ანგარიში',
    equity: 'კაპიტალი',
    marginUsed: 'მარჟაში',
    marginRatio: 'რისკის დონე',
    funding: 'დაფინანსება',
    fundingRate: 'განაკვეთი 8 სთ-ზე',
    nextFunding: 'შემდეგი',
    fundingPaid: 'დაფინანსება',
    protection: 'დაცვა',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'ტეიკ-პროფიტი და სტოპ-ლოსი',
    protectionSheetSubtitle: 'ძრავი პოზიციას ბაზრის ფასით დახურავს, როგორც კი ფასი დონეს შეეხება.',
    clearLevel: 'დონის გარეშე',
    addMargin: 'მარჟის დამატება',
    removeMargin: 'მარჟის მოხსნა',
    marginDelta: 'მარჟის თანხა',
    marginDeltaHint: 'იზოლირებული პოზიცია: მეტი მარჟა — ლიკვიდაცია უფრო შორს',
    addMarginKyc: 'მარჟის დამატება შესაძლებელი გახდება დოკუმენტების დამტკიცების შემდეგ. მარჟის მოხსნა, ტეიკ-პროფიტისა და სტოპ-ლოსის შეცვლა და პოზიციის დახურვა ახლავე შეგიძლიათ.',
    saveProtection: 'შენახვა',
    protectionSaved: 'დაცვა განახლდა',
    protectionFailed: 'პოზიციის განახლება ვერ მოხერხდა',
    protectionInvalid: 'დონე შესვლის ფასის არასწორ მხარესაა',
    leverageTooHigh: 'ბერკეტი ამ წყვილისთვის დასაშვებზე მაღალია',
    leverageTier: (cap: number, notional: string) => `${notional} USDT მოცულობისთვის ბერკეტი ${cap}×-მდე`,
    futuresDisabled: 'ფიუჩერსული ორდერები დროებით არ მიიღება',
    liquidated: 'ლიკვიდირებულია',
    restingToast: (symbol: string, price: string) => `ლიმიტური ორდერი ${symbol} ${price} ფასით განთავსდა და ფასს ელოდება`,
    cancelledToast: 'ორდერი გაუქმდა, თანხა დაბრუნდა',
    costSell: 'მისაღები',
    costBasis: 'ღირებულება',
    enterProtection: 'ტეიკ-პროფიტის ან სტოპ-ლოსის დონე არასწორადაა მითითებული',
    leverageLocked: (leverage: number) => `ღია პოზიციის ბერკეტი — ${leverage}×. მისი შეცვლა მხოლოდ პოზიციის დახურვით შეიძლება`,
    marginReleased: 'დაბრუნდება მარჟა',
    realizedEstimate: 'სავარაუდო შედეგი',
    addsPosition: (quantity: string, asset: string) => `ღია პოზიციას დაემატება ${quantity} ${asset}`,
    reducesPosition: (quantity: string, asset: string) => `ღია პოზიცია შემცირდება ${quantity} ${asset}-ით`,
    closesPosition: (quantity: string, asset: string) => `დაიხურება ღია პოზიცია ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `დაიხურება პოზიცია ${closeQuantity} ${asset} და გაიხსნება ${openQuantity} ${asset} საპირისპირო მიმართულებით`,
    byAccount: 'მთელი ანგარიშით',
    cancelledToastPlain: 'ორდერი გაუქმდა',
    fillBuy: 'ყიდვა',
    fillSell: 'გაყიდვა',
    marginFloor: (floor: string) => `მარჟა საწყისზე დაბლა — ${floor} USDT — ვერ ჩამოვა`,
    marginRemovable: (amount: string) => `შეგიძლიათ მოხსნათ ${amount} USDT-მდე`,
    enterPrice: 'მიუთითეთ ლიმიტური ორდერის ფასი',
    markPrice: 'მარკ-ფასი',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'ფასი მოძველდა — კოტირებების ნაკადი ჩამორჩება. სცადეთ რამდენიმე წამში.',
    marketDisabled: 'ამ წყვილით ვაჭრობა შეჩერებულია',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `${side} ${quantity} ${asset} ${price} ფასით — შესრულდა`,
    closedToast: (symbol: string, pnl: string) => `${symbol} დაიხურა, შედეგი ${pnl} USDT`,
    positionsFailed: 'პოზიციების ჩატვირთვა ვერ მოხერხდა',
    noOrders: 'ლიმიტური ორდერი აქ დაელოდება შესრულებას.',
    ordersFailed: 'ორდერების ჩატვირთვა ვერ მოხერხდა',
    noHistory: 'პირველი შესრულებული გარიგება ამ სიაში გამოჩნდება.',
    historyFailed: 'გარიგებების ისტორიის ჩატვირთვა ვერ მოხერხდა',
    cancelOrder: 'გაუქმება',
    cancelFailed: 'ორდერის გაუქმება ვერ მოხერხდა',
    closeFailed: 'პოზიციის დახურვა ვერ მოხერხდა',
    closeConfirmTitle: 'დავხუროთ პოზიცია?',
    closeConfirmSubtitle:
      'პოზიცია ბაზრის ფასით დაიხურება და მიმდინარე შედეგი საბოლოო გახდება. ამის გაუქმება შეუძლებელია.',
  },

  chart: {
    title: 'გრაფიკი',
    interval: 'ინტერვალი',
    chartType: 'გრაფიკის ტიპი',
    candles: 'სანთლები',
    line: 'ხაზი',
    high24h: 'მაქს. 24სთ',
    low24h: 'მინ. 24სთ',
    volume24h: 'მოცულობა 24სთ',
    empty: 'გრაფიკი გამოჩნდება, როცა ამ წყვილზე მონაცემები დაგროვდება',
    failed: 'გრაფიკის ჩატვირთვა ვერ მოხერხდა',
    unknownPair: 'ასეთი წყვილი არ არსებობს — აირჩიეთ ის ჩანართში „ბაზრები“',
    toMarkets: 'ბაზრებზე',
  },

  security: {
    title: 'უსაფრთხოება',
    subtitle: 'მეორე ფაქტორი იცავს შესვლას და თანხის გატანას',
    statusOn: 'ჩართულია',
    statusOff: 'გამორთულია',
    explain:
      'ავთენტიფიკატორის აპლიკაციის კოდი მოითხოვება შესვლისას და გატანის ყოველ განაცხადზე. მის გარეშე ანგარიში მხოლოდ პაროლითაა დაცული.',
    setup: 'ავთენტიფიკატორის დაკავშირება',
    step1: 'დაასკანერეთ კოდი Google Authenticator-ში, 1Password-ში ან Aegis-ში',
    secretLabel: 'ან შეიყვანეთ საიდუმლო ხელით',
    step2: 'შეიყვანეთ კოდი აპლიკაციიდან',
    enable: 'ჩართვა',
    enabled: 'მეორე ფაქტორი ჩართულია',
    disableTitle: 'მეორე ფაქტორის გამორთვა',
    disableHint: 'შეიყვანეთ მიმდინარე კოდი იმის დასადასტურებლად, რომ ტელეფონი თქვენთანაა',
    disable: 'გამორთვა',
    disabled: 'მეორე ფაქტორი გამორთულია',
    codeInvalid: 'კოდი არასწორია. შეამოწმეთ ტელეფონის დრო და შეიყვანეთ ახალი',
    failed: 'უსაფრთხოების პარამეტრების შეცვლა ვერ მოხერხდა',
    withdrawalCode: 'დადასტურების კოდი',
    withdrawalCodeHint: 'გატანა მეორე ფაქტორითაა დაცული: შეიყვანეთ კოდი აპლიკაციიდან',
    recoveryTitle: 'აღდგენის კოდები',
    recoveryIntro: 'შეინახეთ ეს კოდები საიმედო ადგილას. თითოეული ერთხელ ხსნის შესვლას ან გატანას, თუ ტელეფონი მიუწვდომელია. ისინი მეტად აღარ გამოჩნდება.',
    recoveryCopy: 'კოდების კოპირება',
    recoveryCopied: 'კოდები დაკოპირდა',
    recoverySaved: 'კოდები შევინახე',
    recoveryLeft: (count: number): string => `დარჩენილი აღდგენის კოდები: ${count}`,
    recoveryRegenerate: 'ახალი აღდგენის კოდები',
    recoveryRegenerateHint: 'ძველი კოდები აღარ იმოქმედებს. შეიყვანეთ კოდი აპლიკაციიდან',
    recoveryRegenerated: 'გამოშვებულია ახალი აღდგენის კოდები',
    codeOrRecovery: 'ექვსი ციფრი აპლიკაციიდან ან აღდგენის კოდი',
  },

  addressBook: {
    title: 'გატანის მისამართები',
    hint: 'ახალი მისამართი მუშაობას იწყებს წერილის ბმულით დადასტურების შემდეგ. ასე სხვისი სესია თანხას თავის მისამართზე ვერ გაიტანს.',
    add: 'მისამართის დამატება',
    address: 'მისამართი',
    label: 'დასახელება',
    labelPlaceholder: 'მაგალითად, Ledger',
    send: 'ელფოსტით დადასტურება',
    saved: 'შენახული მისამართები',
    empty: 'ჯერ არცერთი მისამართი',
    emptyBody: 'დაამატეთ მისამართი, რომელზეც თანხა გაგაქვთ: ელფოსტიდან დადასტურების შემდეგ ის გატანის ფორმაში ერთი შეხებით აირჩევა.',
    statusConfirmed: 'დადასტურებულია',
    statusPending: 'ელოდება ელფოსტით დადასტურებას',
    resend: 'ხელახლა გაგზავნა',
    resendLink: 'დადასტურების წერილის ხელახლა გაგზავნა',
    remove: 'მისამართის წაშლა',
    removeTitle: 'წავშალოთ მისამართი?',
    removeHint: 'ამ მისამართზე გატანა შეუძლებელი იქნება, სანამ ხელახლა არ დაამატებთ და დაადასტურებთ.',
    linkSent: 'წერილი გაიგზავნა — გახსენით ბმული მისამართის დასადასტურებლად',
    alreadyConfirmed: 'ეს მისამართი უკვე დადასტურებულია',
    invalid: 'მისამართი არჩეულ ქსელს არ შეესაბამება',
    confirmed: (short: string): string => `მისამართი ${short} დადასტურებულია`,
    confirmFailed: 'ბმული არასწორია ან მოძველდა',
  },

  sessions: {
    title: 'სესიები და მოწყობილობები',
    subtitle: 'სად ხართ შესული თქვენს ანგარიშში',
    current: 'ეს მოწყობილობა',
    started: 'შესვლა',
    lastSeen: 'აქტივობა',
    end: 'დასრულება',
    endOthers: 'ყველა სხვა სესიის დასრულება',
    ended: 'სესია დასრულდა',
    endedOthers: (count: number): string => `დასრულებული სესიები: ${count}`,
    empty: 'სხვა აქტიური სესიები არ არის',
    failed: 'სესიების ჩატვირთვა ვერ მოხერხდა',
    hint: 'დასრულებული სესია წვდომას თხუთმეტ წუთში კარგავს: ამდენ ხანს ცოცხლობს უკვე გაცემული წვდომის გასაღები.',
  },

  profile: {
    title: 'პროფილი',
    accountId: 'ანგარიშის ID',
    idCopied: 'ID დაკოპირდა',
    loadFailed: 'პროფილის ჩატვირთვა ვერ მოხერხდა',
    partnerProgram: 'პარტნიორული პროგრამა',
    partnerProgramHint: 'გუნდი, დონე და დარიცხვები',
    securityOn: 'მეორე ფაქტორი ჩართულია',
    securityOff: 'ჩართეთ მეორე ფაქტორი',

    verification: 'ვერიფიკაცია',
    verificationNone: 'გავლილი არ არის',
    verificationPending: 'მოწმდება',
    verificationInReview: 'მოწმდება',
    verificationApproved: 'გავლილია',
    verificationRejected: 'უარყოფილია',
    verificationResubmit: 'საჭიროა ახალი დოკუმენტები',
    verificationCta: 'დაწყება',
    verificationCtaAgain: 'ხელახლა გაგზავნა',
    verificationPrompt: 'ატვირთეთ პასპორტი — 2 წუთი დასჭირდება',
    verificationPendingHint: 'ოპერატორი დოკუმენტებს ჩვეულებრივ ერთ დღეში ათვალიერებს.',
    verificationRejectedHint: 'ოპერატორმა დოკუმენტები არ მიიღო. განაცხადის ხელახლა გაგზავნა შესაძლებელია.',
    verificationOpen: 'განაცხადის გახსნა',

    withdrawalAddressesHint: 'მისამართების წიგნი ელფოსტით დადასტურებით',
    withdrawalAddresses: 'გატანის მისამართები',
    addressesSubtitle: 'მისამართები, რომლებზეც უკვე გაგიტანიათ თანხა',
    addressesEmpty: 'თანხა ჯერ არ გაგიტანიათ. პირველი განაცხადის მისამართი აქ გამოჩნდება.',
    addressesFailed: 'მისამართების ჩატვირთვა ვერ მოხერხდა',
    addressCopied: 'მისამართი დაკოპირდა',

    security: 'უსაფრთხოება',
    accountAccess: 'ელფოსტა და პაროლი',
    sessions: 'სესიები და მოწყობილობები',
    sessionsHint: 'სად ხართ შესული',
    securitySubtitle: 'ანგარიშში შესვლა',
    securityEmailVerified: 'ელფოსტა დადასტურებულია',
    securityEmailUnverified: 'ელფოსტა დადასტურებული არ არის',
    securityPassword: 'პაროლის შეცვლა',
    securityPasswordHint: 'ელფოსტაზე გამოგიგზავნით ბმულს პაროლის შესაცვლელად',

    notifications: 'შეტყობინებები',
    pushOn: 'Push ჩართ.',
    pushOff: 'Push გამორთ.',
    pushToggle: 'Push-შეტყობინებები',
    pushUnsupported: 'ამ ბრაუზერს push-შეტყობინებები არ შეუძლია',
    pushDenied: 'შეტყობინებები ბრაუზერის პარამეტრებში აკრძალულია',
    pushFailed: 'push-შეტყობინებების ჩართვა ვერ მოხერხდა',
    pushUnavailable: 'push-შეტყობინებები სერვერზე ჯერ არ არის გამართული',


    theme: 'თემა',
    themeHint: 'შავი თემა ბრენდისაა და OLED-ზე ბატარეას ზოგავს. ღია — კაშკაშა მზისთვის. „როგორც სისტემაში“ ტელეფონის პარამეტრს მიჰყვება.',
    themeNames: { system: 'როგორც სისტემაში', dark: 'მუქი', light: 'ღია' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'მიჰყვება მოწყობილობის პარამეტრს',
    language: 'ენა',
    languageHint: 'ინტერფეისის ენა. თანხები ერთიან ფორმატში რჩება; თარიღები ენას მიჰყვება.',

    support: 'მხარდაჭერა',
    supportTitle: 'დაუკავშირდით მხარდაჭერას',
    supportChoose: 'აირჩიეთ დაკავშირების გზა',
    supportVia: 'Telegram ან ელფოსტა',
    supportTelegram: 'მოგვწერეთ Telegram-ში',
    supportEmail: 'მოგვწერეთ ელფოსტაზე',
    about: 'აპლიკაციის შესახებ',
    version: 'ვერსია',

    signOut: 'გასვლა',
    signOutTitle: 'გავიდეთ ანგარიშიდან?',
    signOutBody:
      'ბალანსები, ღია პოზიციები და სიგნალების ისტორია ადგილზე დარჩება — შემდეგი შესვლისას მათ დაუბრუნდებით. სხვა მოწყობილობები ანგარიშში დარჩება.',

    kyc: {
      title: 'ვერიფიკაცია',
      intro:
        'მონაცემებს მხოლოდ შემმოწმებელი ოპერატორი ხედავს. დოკუმენტის ნომერი დაშიფრული ინახება და არსად ჩანს მთლიანად.',
      loadFailed: 'განაცხადის ჩატვირთვა ვერ მოხერხდა',

      document: 'დოკუმენტი',
      documentType: 'დოკუმენტის ტიპი',
      typePassport: 'პასპორტი',
      typeIdCard: 'პირადობის მოწმობა',
      typeDriverLicense: 'მართვის მოწმობა',
      number: 'დოკუმენტის ნომერი',
      numberPlaceholder: '4510 123456',
      numberHint: 'სერია და ნომერი — როგორც დოკუმენტშია',
      country: 'გამცემი ქვეყანა',
      countryPlaceholder: 'აირჩიეთ ქვეყანა',
      issueDate: 'გაცემის თარიღი',
      expiryDate: 'მოქმედების ვადა',

      personal: 'დოკუმენტის მფლობელი',
      lastName: 'გვარი',
      firstName: 'სახელი',
      dateOfBirth: 'დაბადების თარიღი',

      documents: 'დოკუმენტის ფოტო',
      docMain: 'მთავარი გვერდი',
      docMainHint: 'გვერდი ფოტოთი და ნომრით',
      docFront: 'წინა მხარე',
      docFrontHint: 'მხარე ფოტოთი და ნომრით',
      choose: 'ფაილის არჩევა',
      replace: 'შეცვლა',
      remove: 'ამოღება',
      fileHint: (size: string) => `JPG, PNG ან PDF, ${size} მბ-მდე`,
      megabytes: 'მბ',

      submit: 'შესამოწმებლად გაგზავნა',
      submitFailed: 'განაცხადის გაგზავნა ვერ მოხერხდა. შეამოწმეთ მონაცემები და სცადეთ თავიდან',
      uploadFailed:
        'მონაცემები გაიგზავნა, დოკუმენტების ატვირთვა კი ვერ მოხერხდა. სცადეთ მათი ხელახლა მიმაგრება.',
      uploadRetry: 'დოკუმენტების ატვირთვა',
      submitted: 'განაცხადი გაგზავნილია',
      submittedHint: 'ოპერატორი შეამოწმებს დოკუმენტებს და განაახლებს სტატუსს.',

      review: 'განაცხადი მოწმდება',
      reviewHint: 'შემოწმების დროს მონაცემების შეცვლა შეუძლებელია.',
      approved: 'ვერიფიკაცია გავლილია',
      approvedHint: 'ოპერატორმა დოკუმენტები დაადასტურა.',
      rejected: 'განაცხადი უარყოფილია',
      rejectedHint: 'შეასწორეთ მონაცემები და გაგზავნეთ განაცხადი ხელახლა.',
      resubmit: 'საჭიროა ახალი დოკუმენტები',
      resubmitHint: 'ატვირთეთ მოქმედი დოკუმენტი და გაგზავნეთ განაცხადი ხელახლა.',
      rejectionReason: 'მიზეზი',
      submittedAt: (date: string) => `გაგზავნილია ${date}`,
      attached: 'მიმაგრებული დოკუმენტები',
      attachedEmpty: 'განაცხადს არცერთი ფაილი არ ახლავს',
      startNew: 'ხელახლა გაგზავნა',
      step: (current: number, total: number): string => `ნაბიჯი ${current} / ${total}`,

      gate: {

        title: 'გაიარეთ ვერიფიკაცია თანხის გამოსაყენებლად',

        body: 'ანგარიშის შევსება უკვე შესაძლებელია. ვაჭრობა, სიგნალები, გადარიცხვები და გატანა გაიხსნება დოკუმენტების დამტკიცების შემდეგ — ჩვეულებრივ ერთ დღეში.',

        pending: 'დოკუმენტები მოწმდება — წვდომა გაიხსნება დამტკიცების შემდეგ, ჩვეულებრივ ერთ დღეში.',

        action: 'ვერიფიკაციის გავლა',

      },

      kinds: {
        passport_main: 'მთავარი გვერდი',
        passport_registration: 'რეგისტრაციის გვერდი',
        selfie_with_document: 'სელფი დოკუმენტით',
        proof_of_address: 'მისამართის დადასტურება',
      },

      errors: {
        required: 'შეავსეთ ველი',
        number: 'მხოლოდ ლათინური ასოები, ციფრები, ჰარი და დეფისი',
        name: 'მხოლოდ ასოები, ჰარი, აპოსტროფი და დეფისი',
        country: 'აირჩიეთ ქვეყანა',
        date: 'მიუთითეთ თარიღი',
        issueFuture: 'გაცემის თარიღი მომავალში ვერ იქნება',
        expiryPast: 'დოკუმენტის მოქმედების ვადა ამოიწურა',
        expiryBeforeIssue: 'ვადის თარიღი გაცემის თარიღზე გვიანი უნდა იყოს',
        age: 'ვერიფიკაცია ხელმისაწვდომია 18 წლიდან',
        birthAfterIssue: 'დაბადების თარიღი გაცემის თარიღზე ადრე უნდა იყოს',
        fileType: 'გამოდგება JPG, PNG ან PDF ფაილი',
        fileSize: (size: string) => `ფაილი ${size} მბ-ზე მეტია`,
        documentsRequired: 'მიამაგრეთ დოკუმენტის ფოტო ან სკანი',
        fileMissing: 'დაამატეთ ეს ფაილი',
      },
    },
  },

  countries: {
    search: 'ქვეყნის სახელი ან კოდი',
    popular: 'პოპულარული',
    all: 'ყველა ქვეყანა',
    nothing: 'ვერაფერი მოიძებნა. სცადეთ ინგლისური სახელი ან კოდი, მაგალითად DE',
    found: (count: number): string => `ნაპოვნი ქვეყნები: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `პრომო-ბონუსი ${amount} USDT განიბლოკა და სპოტზე ჩაირიცხა`,
    promoExpired: 'პრომო-ბონუსის ვადა ამოიწურა: პირობები დროულად არ შესრულდა',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `მოწვეული ${name} კვალიფიცირებულია: +${signals.text} ბონუს სიგნალი`
        : `მოწვეული ${name} კვალიფიცირებულია`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} ბონუს სიგნალი` : null,
        promo === null ? null : `პრომო-ბონუსი ${promo} USDT გაყინულია პირობის შესრულებამდე`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'მოწვევით კვალიფიკაცია გავლილია' : `კვალიფიკაცია გავლილია: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'კვალიფიკაცია გაუქმდა: გატანის განაცხადი შეკავების ვადის დასრულებამდე. ბონუს სიგნალები და პრომო-ბონუსი გაუქმებულია'
          : 'კვალიფიკაცია გაუქმდა: ბონუს სიგნალები და პრომო-ბონუსი გაუქმებულია'
        : `მოწვეულის ${name} კვალიფიკაცია გაუქმდა: მისთვის მიღებული ბონუს სიგნალები გაუქმებულია`,
    referralRewardPaid: (amount: string): string => `${amount} USDT გადახდილია პარტნიორული პროგრამით`,
    referralRewardRejected: (amount: string): string => `დარიცხვა ${amount} USDT ოპერატორმა უარყო`,
    partnerLevelReached: (level: string, amount: string): string =>
      `მიღწეულია დონე ${level}: ${amount} USDT ბონუსი გადასახდელად დგას`,
    title: 'შეტყობინებები',
    settingsTitle: 'შეტყობინებების პარამეტრები',
    settingsHint: 'გამორთული კატეგორია ისტორიაში რჩება, მაგრამ არ გაწუხებთ: არც ნიშანი, არც push. Push მუშაობს, როცა ის პროფილშია ჩართული.',
    channelInApp: 'აპლიკაციაში',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'რას აგზავნის ბოტი ჩატში. აქ გამორთული კატეგორია Telegram-ში არ მოვა, მაგრამ აპლიკაციაში დარჩება.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram დაკავშირდა' : `Telegram დაკავშირდა: ${name}`),
    telegramLinkedHint: 'თუ ეს თქვენ არ ყოფილხართ, შეეხეთ და გათიშეთ.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram გაითიშა' : `Telegram გაითიშა: ${name}`),
    telegramUnlinkedHint: 'ეს ჩატი სხვა ანგარიშს დაუკავშირდა.',
    categories: { deposit: 'შევსებები', withdrawal: 'გატანები', copy: 'კოპი-ტრეიდინგი', trade: 'გარიგებები და პოზიციები', signal: 'სიგნალები', promo: 'პრომო-ბონუსები', referral: 'პარტნიორული პროგრამა', kyc: 'ვერიფიკაცია', system: 'სისტემური' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'ყველას წაკითხულად მონიშვნა',
    markAllFailed: 'შეტყობინებების წაკითხულად მონიშვნა ვერ მოხერხდა',
    loadFailed: 'შეტყობინებების ჩატვირთვა ვერ მოხერხდა',
    empty:
      'შეტყობინებები ჯერ არ არის. აქ გამოჩნდება ჩარიცხვები, პასუხები გატანის განაცხადებზე, სიგნალები და გარიგებების შედეგები.',
    unread: 'წაუკითხავი',
    today: 'დღეს',
    yesterday: 'გუშინ',
    depositCredited: (amount: string, asset: string) =>
      `ჩაირიცხა ${amount} ${asset}`,
    withdrawalApproved: 'გატანა დამტკიცებულია',
    withdrawalRejected: 'გატანა უარყოფილია',
    withdrawalSent: 'გატანა ქსელში გაიგზავნა',
    marginCall: 'მარჟინ-ქოლი',
    positionLiquidated: 'პოზიცია ლიკვიდირებულია',
    copyOpened: (master: string) => `${master}-მა პოზიცია გახსნა`,
    copyClosed: (master: string) => `${master}-მა პოზიცია დახურა`,
    copyStopLoss: 'გამოწერის სტოპ-ლოსი ამოქმედდა: პოზიციები დაიხურა, თანხა სპოტურ ანგარიშზე დაბრუნდა',
    copyMasterStopped: 'ტრეიდერი შეჩერდა: კოპირება დასრულდა, თანხა სპოტურ ანგარიშზე დაბრუნდა',
    copyReleased: (amount: string) => `სპოტზე დაბრუნდა: ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `კომისია ტრეიდერს: ${commission} USDT ${profit} USDT მოგებიდან` : `კომისია ტრეიდერ ${master}-ს: ${commission} USDT ${profit} USDT მოგებიდან`,
    signalLive: (title: string, until: string) => `სიგნალი „${title}“ ღიაა ${until}-მდე`,
    futuresLiquidated: (symbol: string, price: string) => `პოზიცია ${symbol} ლიკვიდირებულია ${price} ფასით`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: ტეიკ-პროფიტი ამოქმედდა ${price} ფასით`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: სტოპ-ლოსი ამოქმედდა ${price} ფასით`,
    orderFilled: (symbol: string, quantity: string, price: string) => `ლიმიტური ორდერი ${symbol} შესრულდა: ${quantity} ${price} ფასით`,
    orderRejected: (symbol: string) => `ლიმიტური ორდერი ${symbol} არ შესრულდა: ძრავმა გარიგება უარყო`,
    orderRejectedFunds: (symbol: string) => `ლიმიტური ორდერი ${symbol} არ შესრულდა: ფიუჩერსულ ანგარიშზე მარჟა არ ეყო`,
    orderRejectedLeverage: (symbol: string) => `ლიმიტური ორდერი ${symbol} არ შესრულდა: ბერკეტი ამ მოცულობისთვის დასაშვებზე მაღალია`,
    kycApproved: 'ვერიფიკაცია გავლილია',
    kycRejected: 'ვერიფიკაცია უარყოფილია',
    kycResubmit: 'ვერიფიკაციისთვის საჭიროა ახალი დოკუმენტები',
    kycExpiring: (date: string) => `დოკუმენტის ვადა იწურება ${date} — განაახლეთ ვერიფიკაცია`,
    kycExpired: 'დოკუმენტის ვადა ამოიწურა — ატვირთეთ მოქმედი დოკუმენტი',
    signalClosed: (title: string) => `სიგნალი დაიხურა: ${title}`,
    systemAlert: (check: string) => `შეფერხება: ${check}`,
    systemRecovered: (check: string) => `აღდგენილია: ${check}`,
    heads: {
      signalLive: 'სიგნალი გაიხსნა',
      signalClosed: 'სიგნალი დაიხურა',
      orderFilled: 'ლიმიტური ორდერი შესრულდა',
      orderRejected: 'ლიმიტური ორდერი არ შესრულდა',
      takeProfit: 'ტეიკ-პროფიტი ამოქმედდა',
      stopLoss: 'სტოპ-ლოსი ამოქმედდა',
      promoUnlocked: 'პრომო-ბონუსი განიბლოკა',
      promoExpired: 'პრომო-ბონუსის ვადა ამოიწურა',
      referralQualified: 'მოწვეული კვალიფიცირებულია',
      referralWelcome: 'კვალიფიკაცია გავლილია',
      referralRevoked: 'კვალიფიკაცია გაუქმდა',
      rewardPaid: 'ჯილდო გადახდილია',
      rewardRejected: 'დარიცხვა უარყოფილია',
      partnerLevelReached: 'ახალი პარტნიორული დონე',
      kycExpiring: 'დოკუმენტის ვადა იწურება',
      kycExpired: 'დოკუმენტის ვადა ამოიწურა',
      systemAlert: 'შეფერხება',
      systemRecovered: 'აღდგენილია',
    },
    fallback: {
      deposit: 'შევსება ჩაირიცხა',
      withdrawal: 'გატანის განაცხადის განახლება',
      trade: 'პოზიციის მოვლენა',
      copy: 'კოპი-ტრეიდინგის მოვლენა',
      referral: 'პარტნიორული პროგრამის მოვლენა',
      system: 'სისტემური შეტყობინება',
    },
  },

  referrals: {
    title: 'პარტნიორული პროგრამა',
    rewardKind: {
      fee_rebate: 'საკომისიოს წილი',
      signal_grant: 'დამატებითი სიგნალები',
      promotional_credit: 'პრომო-ბონუსი',
      deposit_bonus: 'დეპოზიტის წილი',
      welcome_bonus: 'მისალმების წილი',
      level_bonus: 'დონის ბონუსი',
    },
    rewardStatus: {
      pending_review: 'მოწმდება',
      approved: 'დამტკიცებულია',
      paid: 'გადახდილია',
      rejected: 'უარყოფილია',
      reversed: 'გაუქმებულია',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'გაყინულია',
      unlocked: 'ჩაირიცხა',
      expired: 'დაიწვა',
      revoked: 'გაუქმებულია',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'წევრი',
      bronze: 'ბრინჯაო',
      silver: 'ვერცხლი',
      gold: 'ოქრო',
      platinum: 'პლატინა',
      ambassador: 'ამბასადორი',
    } as Record<string, string>,
    signalUnit: 'სიგნ.',
    membersFrom: (count: Count): string => `${count.text} წევრიდან`,
    baseLevel: 'საწყისი დონე',
    depositFrom: (amount: string): string => `დეპოზიტი ${amount}-დან`,
    noWeeklySignals: 'სიგნალების გარეშე',
    perWeek: (count: Count): string => `${count.text} სიგნ./კვ.`,
    current: 'ახლა',
    copyFailed: 'კოპირება ვერ მოხერხდა',
    inviteText: 'შემოუერთდით 19CRT-ს ჩემი კოდით',
    linkCopied: 'ბმული დაკოპირდა',
    codeCopied: 'კოდი დაკოპირდა',
    loadError: 'პარტნიორული პროგრამის ჩატვირთვა ვერ მოხერხდა',
    loadErrorHint: 'შეამოწმეთ კავშირი და სცადეთ თავიდან',
    retry: 'თავიდან ცდა',
    disabled:
      'პარტნიორული პროგრამა დროებით შეჩერებულია. თქვენი სტატისტიკა და დარიცხვები შენახულია.',
    yourCode: 'თქვენი კოდი',
    codeHint: 'მოიწვიეთ პირდაპირ — პირობები რეგისტრაციამდე ჩანს',
    openQr: 'მოწვევის QR-კოდის გახსნა',
    qrCode: 'QR-კოდი',
    share: 'გაზიარება',
    copy: 'კოპირება',
    yourLevel: 'თქვენი დონე',
    participant: 'მონაწილე',
    allLevels: 'ყველა დონე',
    progressLabel: 'პროგრესი შემდეგ დონემდე',
    qualified: (): string => 'კვალიფიცირებული',
    nextLevel: (remaining: Count, level: string): string => `„${level}“ დონემდე დარჩა ${remaining.text}`,
    maxLevel: 'მაქსიმალური დონე',
    invited: 'მოწვეული',
    confirmed: 'დადასტურებული',
    paidUsdt: 'გადახდილი, USDT',
    signalsReceived: 'მიღებული სიგნალები',
    transparentTitle: 'გამჭვირვალე დარიცხვა',
    transparentHint:
      'ჯილდო ითვლება მხოლოდ რეალურად დაკავებული სავაჭრო საკომისიოდან. მოწვეულის დეპოზიტი არ გადანაწილდება.',
    rewards: 'დარიცხვები',
    rewardHistory: 'ჯილდოების ისტორია',
    underReview: 'მოწმდება',
    emptyRewardsTitle: 'დარიცხვები ჯერ არ არის',
    emptyRewards: 'დარიცხვები გამოჩნდება გუნდის პირველი კვალიფიცირებული აქტივობის შემდეგ.',
    levelsRules: 'დონეები და წესები',
    levelsRulesHint: 'გუნდი, საკომისიოს წილი და სიგნალები',
    inviteTitle: 'მოწვევა 19CRT-ში',
    inviteSubtitle: 'კოდი რეგისტრაციისას მიებმება',
    copyLink: 'ბმულის კოპირება',
    levelsTitle: 'პარტნიორული დონეები',
    levelsSubtitle: 'ითვლება მხოლოდ კვალიფიცირებული პირდაპირი მონაწილეები',
    loadLevelsError: 'დონეების ჩატვირთვა ვერ მოხერხდა',
    levelTeam: 'დონე და გუნდი',
    feeSignals: 'საკომისიო · სიგნალები',
    depositTiersTitle: 'პროცენტები დეპოზიტის ოდენობით',
    depositTiersHint: 'რაც უფრო დიდია პირველი დეპოზიტი, მით მეტია ორივე მხარის წილი',
    tierDeposit: 'დეპოზიტი',
    tierReferrer: 'თქვენ',
    tierWelcome: 'ახალბედას',
    tierFrom: (amount: string): string => `${amount}-დან`,
    levelBonus: 'დონის ბონუსი',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'პროგრამის წესები',
      qualification: (amount: string, asset: string): string =>
        `კვალიფიკაცია — პირველი ჩარიცხული დეპოზიტი მინიმუმ ${amount} ${asset} ერთი ტრანზაქციით.`,
      qualificationKyc: 'საჭიროა გავლილი ვერიფიკაცია.',
      welcome: (signals: Count, days: Count): string =>
        `ახალბედა იღებს ${signals.text} ბონუს სიგნალს ${days.text} დღის ვადით.`,
      referrer: (signals: Count, days: Count): string =>
        `პარტნიორი იღებს ${signals.text} ბონუს სიგნალს ${days.text} დღის ვადით ყოველ კვალიფიცირებულ მოწვეულზე.`,
      promo: (amount: string, required: string, days: Count): string =>
        `პრომო-ბონუსი ${amount} USDT იყინება და სპოტზე ფულად იქცევა, როცა ახალბედას გარიგებების ბრუნვა ${days.text} დღეში ${required} USDT-ს მიაღწევს; წინააღმდეგ შემთხვევაში იწვება.`,
      hold: (days: Count): string =>
        `კვალიფიკაციიდან ${days.text} დღის განმავლობაში ნებისმიერი გატანის განაცხადი აუქმებს კვალიფიკაციას, ბონუს სიგნალებსა და პრომო-ბონუსს.`,
      feeShare:
        'დონის საკომისიო წილი ითვლება მხოლოდ მოწვეულისგან რეალურად დაკავებული სავაჭრო საკომისიოდან. ფულად დარიცხვებს ოპერატორი ამოწმებს და ცალკე იხდის — ბალანსზე ისინი არ ირიცხება.',
      secondLevel: (rate: string): string => `პარტნიორის პარტნიორი იმავე საკომისიოს ${rate}-ს იღებს.`,
      depositTiers:
        'მომწვევისა და ახალბედის პროცენტი დამოკიდებულია პირველი დეპოზიტის ოდენობაზე — იხილეთ ცხრილი ამ ფანჯარაში.',
      balanceShare: (percent: string): string =>
        `დეპოზიტი უნდა იყოს მომწვევის ბალანსის მინიმუმ ${percent} % ჩარიცხვის მომენტისთვის.`,
      topup: (days: Count): string =>
        `პირველიდან ${days.text} დღის განმავლობაში შეტანილი დამატებითი დეპოზიტი ემატება მას, წყვილი ხელახლა ფასდება ცხრილით და სხვაობა ერთხელ ირიცხება.`,
      premiumSignal: (count: string): string =>
        `მუდმივი პრემიუმ-სიგნალი იხსნება, როცა კვალიფიციურ მოწვეულთა რიცხვი ${count} ან მეტია.`,
      antiabuse:
        'პარტნიორთან ან მის გუნდთან მოწყობილობის ან მისამართის დამთხვევა კვალიფიკაციას ოპერატორის შესამოწმებლად აგზავნის.',
      paused: 'პროგრამა ოპერატორმა შეაჩერა: ახალი კვალიფიკაციები და დარიცხვები არ იქმნება.',
    },
    disclaimer:
      'პარტნიორული ჯილდოები გარანტირებული შემოსავალი არ არის. რეგისტრაცია, დეპოზიტები და სავაჭრო ბრუნვა თავისთავად ფულად გადახდას არ ქმნის.',
    team: 'გუნდი',
    teamStatus: {
      pending: 'ელოდება დეპოზიტს',
      flagged: 'ოპერატორი ამოწმებს',
      qualified: 'კვალიფიცირებულია',
      revoked: 'კვალიფიკაცია გაუქმებულია',
    },
    joined: (date: string): string => `${date}-დან`,
    showAll: (count: Count): string => `ყველას ჩვენება (${count.text})`,
    showLess: 'ჩაკეცვა',
    promoTitle: 'პრომო-ბონუსი',
    promoLocked: (amount: string): string => `${amount} USDT გაყინულია`,
    promoUnlocked: (amount: string): string => `${amount} USDT ჩაირიცხა სპოტზე`,
    promoExpired: 'ვადა ამოიწურა, ბონუსი დაიწვა',
    promoRevoked: 'ბონუსი გაუქმებულია',
    promoCondition: (traded: string, required: string): string => `ბრუნვა ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `${date}-მდე`,
    promoHint: 'ბონუსი ფულად იქცევა, როცა გარიგებების ბრუნვა ვადამდე პირობას მიაღწევს.',
  },

  signals: {
    title: 'სიგნალები',
    tabs: { today: 'დღეს', history: 'ისტორია' },
    tabsLabel: 'სიგნალების განყოფილებები',
    access: { public: 'საჯარო', base: 'საბაზისო', premium: 'პრემიუმი', bonus: 'ბონუსი', agent: 'პარტნიორი' },
    status: { scheduled: 'დაგეგმილი', available: 'ხელმისაწვდომი', executed: 'შესრულებული', expired: 'ვადაგასული', cancelled: 'გაუქმებული' },
    side: { buy: 'ყიდვა', sell: 'გაყიდვა' },
    direction: { buy: 'ლონგი · ყიდვა', sell: 'შორტი · გაყიდვა' },
    positionSide: { buy: 'ლონგი', sell: 'შორტი' },
    localTime: 'ადგილობრივი დროით',
    localClock: 'ადგილობრივი დრო',
    timeZone: (offset: string) => `სასაათო სარტყელი ${offset}`,
    reason: {
      unavailable: 'სიგნალი ახლა მიუწვდომელია',
      already: 'ეს სიგნალი უკვე შეასრულეთ',
      expired: 'სიგნალის შესრულების დრო ამოიწურა',
      notReleased: 'სიგნალი ჯერ არ არის გახსნილი',
      cancelled: 'სიგნალი გაუქმებულია',
      balance: 'სპოტურ ანგარიშზე არასაკმარისი თანხაა',
      balanceFutures: 'სპოტურ და ფიუჩერსულ ანგარიშებზე ერთად საკმარისი USDT არ არის',
      positionConflict: 'ამ ბაზარზე საპირისპირო მიმართულების პოზიცია გაქვთ ღია — დახურეთ ის, რომ სიგნალი შეასრულოთ',
      price: 'ამ ბაზრის ფასი ახლა მიუწვდომელია — სცადეთ რამდენიმე წამში',
      access: 'ამ სიგნალისთვის სხვა წვდომის დონეა საჭირო',
      kyc: 'ჯერ გაიარეთ ვერიფიკაცია — დოკუმენტების დამტკიცების შემდეგ სიგნალის შესრულებას შეძლებთ',
      minimum: 'სიგნალი იღებს თქვენი მთლიანი ბალანსის წილს და ის მინიმალურ თანხაზე ნაკლებია. შეავსეთ ბალანსი, რომ შეასრულოთ',
      maximum: 'თანხა ამ სიგნალის მაქსიმუმზე მეტია',
      belowMinimum: (amount: string) => `სიგნალი იღებს თქვენი მთლიანი ბალანსის წილს და ის მინიმუმზე ნაკლებია — ${amount}\u00A0USDT. შეავსეთ ბალანსი, რომ შეასრულოთ`,
      aboveMaximum: (amount: string) => `თანხა ამ სიგნალის მაქსიმუმზე მეტია — მაქსიმუმ ${amount} USDT`,
      notFound: 'ამ კოდით სიგნალი ვერ მოიძებნა',
      generic: 'მოქმედების შესრულება ვერ მოხერხდა. შეამოწმეთ პირობები და სცადეთ თავიდან',
      network: 'სერვერთან დაკავშირება ვერ მოხერხდა. შეამოწმეთ კავშირი და სცადეთ თავიდან',
    },
    enterCode: 'კოდის შეყვანა',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'შეიყვანეთ კოდი არხიდან — დადასტურებამდე ნახავთ მიმართულებას, თანხას და პირობებს', executed: 'შედეგი და ფაქტობრივი პარამეტრები — „ისტორიის“ ჩანართში', scheduled: (time: string) => `კოდი არხში ${time}-ზე გამოჩნდება` },
    executing: 'სრულდება',
    notExecuted: 'შეუსრულებელი',
    executedLabel: 'შესრულებულია',
    loadSignalsError: 'სიგნალების ჩატვირთვა ვერ მოხერხდა',
    noSignalsToday: 'დღეს სიგნალები არ არის',
    loadErrorHint: 'შეამოწმეთ კავშირი და სცადეთ ხელახლა',
    retry: 'თავიდან ცდა',
    signalAvailable: 'სიგნალი ხელმისაწვდომია',
    nextSignal: 'შემდეგი სიგნალი',
    windowOpenUntil: (time: string) => `ფანჯარა ღიაა ${time}-მდე`,
    opensAt: (time: string) => `${time}-ზე გაიხსნება`,
    execute: 'შესრულება',
    doneToday: 'დღეისთვის სულ ესაა',
    newBySchedule: 'ახალი სიგნალები განრიგით გამოჩნდება',
    codeOverline: 'სიგნალის კოდი',
    checkTerms: 'პირობების შემოწმება',
    codeHint: 'კოდი ქვეყნდება ჩვენს Telegram-არხზე — შეიყვანეთ ის, როგორც კი დაინახავთ.',
    openChannel: 'არხის გახსნა',
    channelLink: 'Telegram-არხზე',
    panelTitle: 'გარიგების კოდი',
    dealsTitle: 'გარიგებები კოდით',
    dealsEmpty: 'კოდით გარიგებები ჯერ არ არის',
    dealsEmptyHint: 'შეიყვანეთ კოდი არხიდან — გარიგება და მისი შედეგი აქ გამოჩნდება',
    codePlaceholder: 'მაგალითად, 1QF9ET7RQ',
    codeRequired: 'შეიყვანეთ კოდი არხიდან',
    codeLabel: 'სიგნალის კოდი',
    check: 'შემოწმება',
    schedule: 'განრიგი',
    todaySignals: 'დღევანდელი სიგნალები',
    noSignals: 'ახლა სიგნალები არ არის. ახალი კოდები განრიგით ქვეყნდება ჩვენს Telegram-არხზე.',
    riskDisclosure:
      'სიგნალი სავაჭრო იდეაა და არა გარანტირებული შედეგი. დადასტურებამდე ნახავთ მიმართულებას, თანხას, ბერკეტს, საკომისიოს და დახურვის პირობებს.',
    loadHistoryError: 'სიგნალების ისტორიის ჩატვირთვა ვერ მოხერხდა',
    historyEmpty: 'ისტორია ჯერ ცარიელია',
    historyEmptyHint: 'აქ გამოჩნდება ყველა შესრულებული სიგნალი და მათი ფაქტობრივი პარამეტრები.',
    toSignals: 'სიგნალებზე',
    allExecutions: 'ყველა შესრულება',
    signalAccepted: 'სიგნალი მიღებულია',
    confirmation: 'სიგნალის დადასტურება',
    sentToTrading: 'ორდერი სავაჭრო სისტემას გადაეცა',
    checkBeforeExecute: 'შესრულებამდე შეამოწმეთ პირობები',
    acceptedHint: 'ხელახლა დაჭერა მეორე ორდერს არ შექმნის.',
    ready: 'მზადაა',
    account: 'ანგარიში',
    spot: 'სპოტური',
    futures: 'ფიუჩერსული',
    perpetualMarket: 'ფიუჩერსი',
    validUntil: 'მოქმედების ვადა',
    signalName: 'სიგნალი',
    until: (time: string) => `${time}-მდე`,
    timeLeft: (countdown: string) => `დარჩა ${countdown}`,
    priceGuide: 'ფასის ორიენტირი',
    entryGuide: 'შესვლის ორიენტირი',
    marketPrice: 'საბაზრო',
    stopLoss: 'სტოპ-ლოსი',
    takeProfit: 'ტეიკ-პროფიტი',
    autoClose: 'თავად დაიხურება',
    inDuration: (duration: string) => inTime(duration),
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0დღ.` : null, hours > 0 ? `${hours}\u00A0სთ` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0წთ` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'უკვე შესრულებულია',
    executionUnavailable: 'შესრულება მიუწვდომელია',
    executionAmount: 'შესრულების თანხა',
    marginAmount: 'მარჟა',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `მთლიანი ბალანსის ${share} · ${total}\u00A0USDT`,
    estimatedFee: 'სავარაუდო საკომისიო',
    totalDebit: 'სულ ჩამოსაჭრელი',
    positionSize: 'პოზიციის ზომა',
    willSell: 'გაიყიდება',
    fundingNote: (amount: string) =>
      `ამ გარიგებისთვის სპოტური ანგარიშიდან ფიუჩერსულზე გადაირიცხება ${amount} USDT. პოზიციის დახურვისას ეს თანხა, შედეგის გათვალისწინებით, სპოტურ ანგარიშზე დაბრუნდება.`,
    executionRisk:
      'სიგნალი სავაჭრო იდეაა და არა გარანტირებული შედეგი. შესრულების ფასი შეიძლება სავარაუდოსგან განსხვავდებოდეს, გარიგებამ კი შეიძლება ზარალი მოიტანოს. დაადასტურეთ მხოლოდ თქვენთვის გასაგები რისკი.',
    executionRiskFutures:
      'სიგნალი სავაჭრო იდეაა და არა გარანტირებული შედეგი. შესვლის ფასი შეიძლება სავარაუდოსგან განსხვავდებოდეს, ბერკეტი კი აძლიერებს როგორც მოგებას, ისე ზარალს — პოზიციის ლიკვიდაციამდე. დაადასტურეთ მხოლოდ თქვენთვის გასაგები რისკი.',
    retryExecution: 'შესრულების გამეორება',
    outcomeLabel: 'შედეგი, USDT',
    outcomeOpen: 'მიმდინარეობს',
    outcome: { take_profit: 'დაიხურა მიზანზე', stop_loss: 'დაიხურა სტოპზე', time: 'დაიხურა დროით', manual: 'დაიხურა ხელით', liquidation: 'დაიხურა ლიკვიდაციით' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'მიზანზე', stop_loss: 'სტოპზე', time: 'დროით', manual: 'ხელით', liquidation: 'ლიკვიდაცია' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'მიმდინარე',
    executeFor: 'შესრულება არაუმეტეს',
    openLong: (amount: string) => `ლონგის გახსნა · ${amount} USDT`,
    openShort: (amount: string) => `შორტის გახსნა · ${amount} USDT`,
    submittedToast: 'სიგნალი შესასრულებლად მიღებულია',
    filledOf: (requested: string, filled: string) =>
      `მოთხოვნილი იყო ${requested} USDT, შესრულდა ${filled} USDT — მოცულობა ლოტის ბიჯამდე დამრგვალდა.`,
    marginRounded: (requested: string) => `მოთხოვნილი იყო ${requested} USDT — მოცულობა ლოტის ბიჯამდე დამრგვალდა.`,
    longOpened: (margin: string, leverage: number) => `ლონგი გაიხსნა: მარჟა ${margin} USDT, ბერკეტი ${leverage}×.`,
    shortOpened: (margin: string, leverage: number) => `შორტი გაიხსნა: მარჟა ${margin} USDT, ბერკეტი ${leverage}×.`,
    fundedFromSpot: (amount: string) => `სპოტური ანგარიშიდან გადაირიცხა ${amount} USDT.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'ტეიკ-პროფიტით' : null, stopLoss ? 'სტოპ-ლოსით' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `თავად დაიხურება ${inTime(duration)}`
      const exits = duration === null ? levels : [...levels, inTime(duration)]
      const last = exits[exits.length - 1] ?? ''
      return `დაიხურება ${exits.length === 1 ? last : `${exits.slice(0, -1).join(', ')} ან ${last}`}`
    },
    openPosition: 'პოზიციაზე გადასვლა',
    returnedToSpot: (amount: string) => `სპოტზე დაბრუნდა: ${amount} USDT`,
    positionAccepted: 'პოზიცია გაიხსნა ფიუჩერსულ ანგარიშზე',
  },

  notFound: {
    title: 'გვერდი ვერ მოიძებნა',
    message: 'ასეთი გვერდი აქ არ არის — ბმული მოძველდა ან მისამართში შეცდომაა.',
    action: 'მთავარზე',
  },
}
